import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	Box, Button, Chip, ClickAwayListener, Divider, FormControl,
	MenuItem, MenuList, Paper, Popper, Select,
	ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import BookmarkBorderIcon    from '@mui/icons-material/BookmarkBorder'
import ViewSidebarIcon       from '@mui/icons-material/ViewSidebar'
import CompareArrowsIcon     from '@mui/icons-material/CompareArrows'
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt'
import { DatePicker }        from '@mui/x-date-pickers'
import moment                from 'moment'

import {
	setAggLevel, setDateFrom, setDateTo,
	setTech1, setTech2, setNumCharts,
} from '../../reducers/filtersReducer'
import { applyAndFetchKpiData, fetchTech2Data } from '../../actions/kpiDataActions'
import { clearTechData }                          from '../../reducers/kpiDataReducer'
import LockdownKPIDashboardExportButton           from '../charts/LockdownKPIDashboardExportButton'

const AGG_LEVELS = ['Cluster', 'Side', 'Polygon', 'Manager', 'Optimiser', 'Vendor', 'JV_ID', 'Cell Name']
const TECHS       = ['5G', '4G', '3G', '2G']
const TECH_COLORS = {
	'5G': { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' },
	'4G': { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
	'3G': { bg: '#fff3e0', color: '#bf360c', border: '#ffcc80' },
	'2G': { bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
}

function Label({ children }) {
	return (
		<Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '3px' }}>
			{children}
		</Typography>
	)
}

export default function LockdownKPIDashboardFilterBar({ onOpenPicker, showMarks, onToggleMarks, showLegend, onToggleLegend, gridRef, lockdownTitle }) {
	const dispatch = useDispatch()
	const selected = useSelector(s => s.lockdown.selected)
	const filters  = useSelector(s => s.filters)
	const loading  = useSelector(s => s.kpiData.loadingKeys.length > 0)

	const tech1Ref   = useRef(null)
	const compareRef = useRef(null)
	const [tech1Popover, setTech1Popover] = useState(false)
	const [tech2Popover, setTech2Popover] = useState(false)

	const { tech1, tech2, numCharts } = filters
	const comparing = tech2 !== null

	// ── Tech 1 ────────────────────────────────────────────────────────────
	const handleSelectTech1 = (tech) => {
		setTech1Popover(false)
		if (tech === tech1) return
		dispatch(clearTechData(tech1))
		dispatch(setTech1(tech))
		dispatch(applyAndFetchKpiData())
	}

	// ── Tech 2 (Compare) ──────────────────────────────────────────────────
	const handleRemoveCompare = () => {
		dispatch(clearTechData(tech2))
		dispatch(setTech2(null))
		dispatch(applyAndFetchKpiData())
		setTech2Popover(false)
	}

const handleSelectTech2 = (tech) => {
    setTech2Popover(false)
    if (tech === tech2) return
    if (tech2) dispatch(clearTechData(tech2))
    dispatch(setTech2(tech))
    setTimeout(() => dispatch(applyAndFetchKpiData()), 0)
}

	const btnSx = (active) => ({
		height: 28, px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
		border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
		borderRadius: '6px !important', gap: 0.5,
		'&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.main' },
	})

	const techChipSx = (tech) => {
		const c = TECH_COLORS[tech] ?? TECH_COLORS['4G']
		return {
			height: 28, px: 1.25, fontSize: 11, fontWeight: 600, textTransform: 'none',
			gap: 0.5, minWidth: 0,
			bgcolor: c.bg, color: c.color,
			border: `1px solid ${c.border}`,
			'&:hover': { bgcolor: c.bg, borderColor: c.color, filter: 'brightness(0.97)' },
		}
	}

	return (
		<Paper elevation={0} square sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>

			{/* ══ ROW 1: Lockdown · Dates · Aggregation · Apply ═════════════════ */}
			<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 2.5, pt: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>

				{/* Lockdown */}
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Label>Lockdown</Label>
					<Chip
						label={selected?.lockdownName ?? 'None selected'}
						onClick={onOpenPicker}
						deleteIcon={<Typography sx={{ fontSize: 10, mr: 0.5 }}>✎</Typography>}
						onDelete={onOpenPicker}
						sx={{
							height: 28, fontSize: 11, fontWeight: 600,
							bgcolor: 'primary.light', color: 'primary.dark',
							border: '1px solid', borderColor: 'primary.main', borderRadius: 1.5,
							cursor: 'pointer', maxWidth: 300,
							'&:hover': { bgcolor: '#bbdefb' },
							'& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
						}}
					/>
				</Box>

				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

				{/* Dates */}
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Label>First Day</Label>
					<DatePicker
						format="YYYY-MM-DD"
						value={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD') : null}
						onChange={v => dispatch(setDateFrom(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
						maxDate={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD').subtract(1, 'day') : undefined}
						slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
					/>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Label>Last Day</Label>
					<DatePicker
						format="YYYY-MM-DD"
						value={filters.dateTo ? moment(filters.dateTo, 'YYYY-MM-DD') : null}
						onChange={v => dispatch(setDateTo(v?.isValid() ? v.format('YYYY-MM-DD') : ''))}
						minDate={filters.dateFrom ? moment(filters.dateFrom, 'YYYY-MM-DD').add(1, 'day') : undefined}
						slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { height: 28, fontSize: 11 } } } }}
					/>
				</Box>

				<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

				{/* Aggregation — global, no auto-fetch on change */}
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Label>Aggregation</Label>
					<ToggleButtonGroup
						value={filters.aggLevel} exclusive size="small"
						onChange={(_, v) => { if (v) dispatch(setAggLevel(v)) }}
						sx={{ height: 28 }}
					>
						{AGG_LEVELS.map(l => (
							<ToggleButton key={l} value={l} sx={{
								px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
								'&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
							}}>{l}</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Box>

				{/* Single Apply — applies dates + aggregation together */}
				<Button variant="contained" size="small" disabled={!selected || loading}
					onClick={() => dispatch(applyAndFetchKpiData())}
					sx={{ height: 28, alignSelf: 'flex-end', px: 2.5 }}>
					Apply
				</Button>
			</Box>

			{/* ══ ROW 2: View (Marks/Legend/Tech1/Compare/Charts) · Export ══════ */}
			<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 2.5, pt: 0.75, pb: 1, flexWrap: 'wrap' }}>

				{/* View */}
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Label>View</Label>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>

						<ToggleButton value="marks" selected={showMarks} onChange={onToggleMarks} size="small" sx={btnSx(showMarks)}>
							<BookmarkBorderIcon sx={{ fontSize: 13 }} />
							Marks
						</ToggleButton>

						<ToggleButton value="legend" selected={showLegend} onChange={onToggleLegend} size="small" sx={btnSx(showLegend)}>
							<ViewSidebarIcon sx={{ fontSize: 13 }} />
							Legend
						</ToggleButton>

						{/* Tech 1 */}
						<Button ref={tech1Ref} onClick={() => setTech1Popover(v => !v)} size="small"
							variant="outlined"
							sx={tech1 ? techChipSx(tech1) : {
								height: 28, px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
								gap: 0.5, minWidth: 0, borderColor: 'divider', color: 'text.secondary',
								'&:hover': { borderColor: 'primary.main', color: 'primary.main' },
							}}>
							<SignalCellularAltIcon sx={{ fontSize: 13 }} />
							{tech1 ?? 'Tech 1'}
						</Button>

						<Popper open={tech1Popover} anchorEl={tech1Ref.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
							<ClickAwayListener onClickAway={() => setTech1Popover(false)}>
								<Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 130 }}>
									<Box sx={{ px: 1.5, py: '6px', borderBottom: '1px solid', borderColor: 'divider' }}>
										<Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
											Primary Tech
										</Typography>
									</Box>
									<MenuList dense>
										{TECHS.map(t => {
											const c = TECH_COLORS[t]
											return (
												<MenuItem key={t} onClick={() => handleSelectTech1(t)} selected={tech1 === t}
													sx={{ gap: 1, '&.Mui-selected': { bgcolor: 'primary.light' } }}>
													<Chip label={t} size="small"
														sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 10, height: 20, borderRadius: 1 }} />
												</MenuItem>
											)
										})}
									</MenuList>
								</Paper>
							</ClickAwayListener>
						</Popper>

						{/* Compare */}
						<Button ref={compareRef} onClick={() => setTech2Popover(v => !v)} size="small"
							variant={comparing ? 'contained' : 'outlined'}
							sx={{
								height: 28, px: 1.25, fontSize: 11, fontWeight: 500, textTransform: 'none',
								gap: 0.5, minWidth: 0,
								...(comparing ? {
									bgcolor: 'primary.main', color: '#fff',
									'&:hover': { bgcolor: 'primary.dark' },
								} : {
									borderColor: 'divider', color: 'text.secondary',
									'&:hover': { borderColor: 'primary.main', color: 'primary.main' },
								})
							}}>
							<CompareArrowsIcon sx={{ fontSize: 13 }} />
							{comparing && tech2 ? tech2 : 'Compare'}
						</Button>

						<Popper open={tech2Popover} anchorEl={compareRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
							<ClickAwayListener onClickAway={() => setTech2Popover(false)}>
								<Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minWidth: 130 }}>
									<Box sx={{ px: 1.5, py: '6px', borderBottom: '1px solid', borderColor: 'divider' }}>
										<Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
											Compare with
										</Typography>
									</Box>
									<MenuList dense>
										{TECHS.map(t => {
											const c = TECH_COLORS[t]
											return (
												<MenuItem key={t} onClick={() => handleSelectTech2(t)} selected={tech2 === t}
													sx={{ gap: 1, '&.Mui-selected': { bgcolor: 'primary.light' } }}>
													<Chip label={t} size="small"
														sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: 10, height: 20, borderRadius: 1 }} />
												</MenuItem>
											)
										})}
										{comparing && (
											<MenuItem onClick={handleRemoveCompare}
												sx={{ gap: 1, borderTop: '1px solid', borderColor: 'divider', color: 'error.main' }}>
												<Typography sx={{ fontSize: 11 }}>✕ Remove Compare</Typography>
											</MenuItem>
										)}
									</MenuList>
								</Paper>
							</ClickAwayListener>
						</Popper>

						<Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 20, alignSelf: 'center' }} />

						<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							<Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 500 }}>Charts</Typography>
							<FormControl size="small">
								<Select value={numCharts} onChange={e => dispatch(setNumCharts(Number(e.target.value)))}
									sx={{ height: 28, fontSize: 11, minWidth: 52, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
									{[1, 2, 3].map(n => <MenuItem key={n} value={n} sx={{ fontSize: 11 }}>{n}</MenuItem>)}
								</Select>
							</FormControl>
						</Box>

					</Box>
				</Box>

				<Box sx={{ flex: 1 }} />

				{/* Export */}
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Label>Export</Label>
					<LockdownKPIDashboardExportButton containerRef={gridRef} title={lockdownTitle ?? 'Lockdown_KPI_Dashboard'} />
				</Box>
			</Box>
		</Paper>
	)
}
